import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { api } from '@/api/core';
import {
  activeUsersReducer,
  bundleBusyTasksReducer,
  bundleCategoryListReducer,
  bundleDownloadReducer,
  bundleFilterOptionsReducer,
  bundlePageInfoReducer,
  bundleReducer,
  bundleRetrieveReducer,
  bundleSearchReducer,
  bundleSectionsReducer,
  bundleSectionsSuggestionsReducer,
  bundleViewportDataReducer,
  caseDocumentsReducer,
  caseOverviewReducer,
  caseProvidersReducer,
  caseReducer,
  casesBasicReducer,
  casesFiltersReducer,
  casesReducer,
  caseUploadedMedicalRecordsReducer,
  deactivatedUsersReducer,
  endpointLocksReducer,
  gdprDocumentReducer,
  gdprColumnSizeReducer,
  medicalMatchReducer,
  medicalRecordsFiltersReducer,
  medicalRecordsReducer,
  medicalRecordsUploadedDocumentsReducer,
  networkStatusReducer,
  serverLastCommunicationReducer,
  sharedDataReducer,
  timelineChronologyReducer,
  userAboutReducer,
  userManagementReducer,
  usersListReducer,
} from './slices';

export const store = configureStore({
  reducer: {
    [api.reducerPath]: api.reducer,
    activeUsers: activeUsersReducer,
    bundle: bundleReducer,
    bundleBusyTasks: bundleBusyTasksReducer,
    bundleCategoryList: bundleCategoryListReducer,
    bundleDownload: bundleDownloadReducer,
    bundleFilterOptions: bundleFilterOptionsReducer,
    bundlePageInfo: bundlePageInfoReducer,
    bundleRetrieve: bundleRetrieveReducer,
    bundleSearch: bundleSearchReducer,
    bundleSections: bundleSectionsReducer,
    bundleSectionsSuggestions: bundleSectionsSuggestionsReducer,
    bundleViewportData: bundleViewportDataReducer,
    case: caseReducer,
    caseDocuments: caseDocumentsReducer,
    caseOverview: caseOverviewReducer,
    caseProviders: caseProvidersReducer,
    cases: casesReducer,
    casesBasic: casesBasicReducer,
    casesFilters: casesFiltersReducer,
    caseUploadedMedicalRecords: caseUploadedMedicalRecordsReducer,
    deactivatedUsers: deactivatedUsersReducer,
    endpointLocks: endpointLocksReducer,
    gdprDocument: gdprDocumentReducer,
    gdprColumnSize: gdprColumnSizeReducer,
    medicalMatch: medicalMatchReducer,
    medicalRecords: medicalRecordsReducer,
    medicalRecordsFilters: medicalRecordsFiltersReducer,
    medicalRecordsUploadedDocuments: medicalRecordsUploadedDocumentsReducer,
    networkStatus: networkStatusReducer,
    serverLastCommunication: serverLastCommunicationReducer,
    sharedData: sharedDataReducer,
    timelineChronology: timelineChronologyReducer,
    userAbout: userAboutReducer,
    userManagement: userManagementReducer,
    usersList: usersListReducer,
  },
  /**
   * Adding the api middleware enables caching, invalidation, polling,
   * and other useful features of `rtk-query`.
   */
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(api.middleware),
});

/**
 * Infer the `RootState` and `AppDispatch` types from the store itself
 */
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;

/**
 * optional, but required for refetchOnFocus/refetchOnReconnect behaviors
 * see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
 */
setupListeners(store.dispatch);
